/**
 * TOSS Web Application - i18n Store
 *
 * Lightweight in-app dictionary with per-key overrides persisted to
 * localStorage. Keys and their default values live in
 * `src/i18n/defaults.ts`; users can override any value at runtime
 * (e.g. via the meme term selector in the header).
 *
 * Usage:
 * ```tsx
 * const t = useT();
 * <h1>{t('equipment.label')}</h1>
 * ```
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { I18N_DEFAULTS, type I18nKey } from '@/i18n/defaults';

interface I18nState {
  overrides: Partial<Record<I18nKey, string>>;
}

interface I18nActions {
  setOverride: (key: I18nKey, value: string) => void;
  clearOverride: (key: I18nKey) => void;
  clearAllOverrides: () => void;
}

type I18nStore = I18nState & I18nActions;

export const useI18nStore = create<I18nStore>()(
  persist(
    (set) => ({
      overrides: {},

      setOverride: (key, value) => {
        const trimmed = value.trim();
        set((state) => {
          if (!trimmed) {
            const next = { ...state.overrides };
            delete next[key];
            return { overrides: next };
          }
          return { overrides: { ...state.overrides, [key]: trimmed } };
        });
      },

      clearOverride: (key) => {
        set((state) => {
          if (!(key in state.overrides)) return state;
          const next = { ...state.overrides };
          delete next[key];
          return { overrides: next };
        });
      },

      clearAllOverrides: () => set({ overrides: {} }),
    }),
    {
      name: 'toss-i18n-store',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

/**
 * Resolve a key against current overrides, falling back to the
 * built-in default.
 */
export function translate(key: I18nKey): string {
  const override = useI18nStore.getState().overrides[key];
  return override ?? I18N_DEFAULTS[key];
}

/**
 * Hook returning a reactive translator. Components re-render when
 * an override for any key they reference changes.
 */
export function useT(): (key: I18nKey) => string {
  const overrides = useI18nStore((state) => state.overrides);
  return (key) => overrides[key] ?? I18N_DEFAULTS[key];
}

/**
 * Hook returning the value for a single key. Slightly cheaper than
 * useT() when a component only needs one term.
 */
export function useTerm(key: I18nKey): string {
  return useI18nStore((state) => state.overrides[key] ?? I18N_DEFAULTS[key]);
}
